import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Briefcase, Users, CheckCircle, Clock, MapPin, TrendingUp, 
  AlertCircle, Calendar, Filter, RefreshCw, Navigation, Map as MapIcon
} from 'lucide-react';
import { getDashboardStats, getWorkersLocations, getAllLocalAssignments } from '../../../models/mmsModel';
import JobCardStatsWidget from '../../../components/mms/stats/JobCardStatsWidget';
import WorkersLocationMap from '../../../components/mms/WorkersLocationMap';
import { useMmsLookups } from '../../../hooks/useMmsLookups';

/**
 * Enhanced MMS Dashboard
 * Enterprise-level overview with real-time statistics and quick actions
 */
const EnhancedMMSDashboard = () => {
  const navigate = useNavigate();
  const { lookups, loading: lookupsLoading } = useMmsLookups();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false); // Separate state for background refresh
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    stats: { local: {} },
    recent_assignments: [],
    all_assignments: [], // Store all local assignments for stats consistency
    workers_locations: []
  });
  const [quickFilters, setQuickFilters] = useState({
    pending: 0,
    assigned: 0,
    inProgress: 0,
    paused: 0,
    completed: 0,
    emergency: 0,
    urgent: 0
  });

  useEffect(() => {
    fetchDashboardData(true); // Initial load with loading state
    const interval = setInterval(() => fetchDashboardData(false), 300000); // Refresh every 5 minutes (reduced from 1 minute)
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async (isInitialLoad = false) => {
    try {
      // Only show loading spinner on initial load, use refreshing state for background updates
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      setError(null);

      // Fetch data from multiple sources - LOCAL ASSIGNMENTS ONLY
      const [mmsData, localAssignmentsData, workersLocationsData] = await Promise.all([
        getDashboardStats().catch(() => ({ stats: {}, workers_locations: [] })),
        getAllLocalAssignments().catch((err) => {
          console.error('Error fetching local assignments:', err);
          return { data: { assignments: [] } };
        }),
        getWorkersLocations().catch((err) => {
          console.error('Error fetching workers locations:', err);
          return [];
        })
      ]);

      // Extract local assignments from API response
      const assignmentsList = localAssignmentsData?.data?.assignments || 
                             localAssignmentsData?.assignments || 
                             (Array.isArray(localAssignmentsData) ? localAssignmentsData : []);
      
      console.log('=== DASHBOARD DATA VERIFICATION (LOCAL ASSIGNMENTS) ===');
      console.log('Fetched local assignments count:', assignmentsList.length);
      console.log('Workers locations count:', Array.isArray(workersLocationsData) ? workersLocationsData.length : 'Not an array');
      
      // Calculate local stats from local assignments
      const localStats = calculateLocalStats(assignmentsList);
      const quickFilterCounts = calculateQuickFilters(assignmentsList);

      console.log('Calculated quick filter counts:', quickFilterCounts);
      console.log('Local stats:', localStats);
      console.log('=== END VERIFICATION ===');

      // Extract workers locations from API response
      const workersLocationsArray = Array.isArray(workersLocationsData) 
        ? workersLocationsData 
        : workersLocationsData?.data || [];

      // Merge backend stats with calculated stats
      const mergedStats = {
        ...mmsData.stats,
        ...localStats
      };

      setDashboardData({
        stats: { local: mergedStats },
        workers_locations: workersLocationsArray,
        all_assignments: assignmentsList, // Store all assignments for consistent stats
        recent_assignments: assignmentsList.slice(0, 10) // Only show top 10 in list
      });
      setQuickFilters(quickFilterCounts);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError(err.message);
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      } else {
        setRefreshing(false);
      }
    }
  };

  const calculateLocalStats = (assignments) => {
    if (!Array.isArray(assignments) || assignments.length === 0) {
      return {
        total_workers: 0,
        active_assignments: 0,
        total_assignments: 0,
        completed_assignments: 0
      };
    }

    const uniqueWorkers = new Set();
    let activeAssignments = 0;
    let completedAssignments = 0;
    let assignedCount = 0;
    let pendingCount = 0;

    assignments.forEach(assignment => {
      // Count unique workers
      if (assignment.worker_name && assignment.worker_name.trim() !== '') {
        uniqueWorkers.add(assignment.worker_name);
      }
      if (assignment.ASSIGNED_TO_NAME && assignment.ASSIGNED_TO_NAME.trim() !== '') {
        uniqueWorkers.add(assignment.ASSIGNED_TO_NAME);
      }

      // Count by LOCAL assignment status only (not EDAMS OM_STATUS)
      const status = (assignment.assignment_status || assignment.status || '').toLowerCase();

      if (status === 'active' || status === 'in-progress' || status === 'in_progress') {
        activeAssignments++;
      } else if (status === 'completed') {
        completedAssignments++;
      } else if (status === 'assigned') {
        assignedCount++;
      } else if (status === 'pending') {
        pendingCount++;
      }
    });

    return {
      total_workers: uniqueWorkers.size,
      active_assignments: activeAssignments + assignedCount, // Include assigned in active
      total_assignments: assignments.length,
      completed_assignments: completedAssignments,
      assigned_assignments: assignedCount,
      pending_assignments: pendingCount
    };
  };

  const calculateQuickFilters = (assignments) => {
    const filters = {
      pending: 0,
      assigned: 0,
      inProgress: 0,
      paused: 0,
      completed: 0,
      emergency: 0,
      urgent: 0
    };

    if (!assignments || assignments.length === 0) {
      return filters;
    }

    assignments.forEach(assignment => {
      // Count by LOCAL assignment status only (not EDAMS OM_STATUS)
      const status = (assignment.assignment_status || assignment.status || '').toLowerCase();

      // Map local status to filters
      if (status === 'pending') {
        filters.pending++;
      } else if (status === 'assigned') {
        filters.assigned++;
      } else if (status === 'in-progress' || status === 'in_progress' || status === 'active') {
        filters.inProgress++;
      } else if (status === 'paused' || status === 'on-hold' || status === 'on_hold') {
        filters.paused++;
      } else if (status === 'completed') {
        filters.completed++;
      }

      // Count by priority if available from EDAMS data (for display only)
      const priorityName = (assignment.PRIORITY_NAME || '').toLowerCase().trim();
      
      if (priorityName === 'emergency') {
        filters.emergency++;
      }
      if (priorityName === 'urgent') {
        filters.urgent++;
      }
    });

    return filters;
  };


  const navigateWithFilter = (filterType, filterValue) => {
    // Navigate to local assignments tab in job cards view
    navigate('/dashboard/mms/jobcards', { state: { filter: { [filterType]: filterValue }, activeTab: 'local' } });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-ZA', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid';
    }
  };

  if (loading && !dashboardData.recent_assignments.length) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 py-3">
        {/* Header with Integrated Quick Filters and Stats */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-3">
          {/* Top Row: Title, Stats, and Refresh */}
          <div className="flex items-center justify-between mb-4">
            {/* Left: Title */}
            <div className="flex items-center">
              <div className="flex-shrink-0 rounded-lg p-2 mr-3" style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <Briefcase className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">MMS Dashboard</h1>
                <p className="text-xs text-gray-500">Mobile Maintenance System</p>
              </div>
            </div>

            {/* Center: Compact Stats */}
            <div className="hidden lg:flex items-center gap-4">
              {/* Total Local Assignments */}
              <div className="flex flex-col items-center px-3 py-1 bg-blue-50 rounded-lg border border-blue-200">
                <span className="text-xs text-blue-700 font-medium">Total Assignments</span>
                <span className="text-lg font-bold text-blue-800">
                  {dashboardData.stats.local.total_assignments || dashboardData.all_assignments.length}
                </span>
              </div>

              {/* Active Assignments */}
              <div className="flex flex-col items-center px-3 py-1 bg-green-50 rounded-lg border border-green-200">
                <span className="text-xs text-green-700 font-medium">Active Assignments</span>
                <span className="text-lg font-bold text-green-800">
                  {dashboardData.stats.local.active_assignments || 0}
                </span>
              </div>

              {/* Completed Assignments */}
              <div className="flex flex-col items-center px-3 py-1 bg-purple-50 rounded-lg border border-purple-200">
                <span className="text-xs text-purple-700 font-medium">Completed</span>
                <span className="text-lg font-bold text-purple-800">
                  {dashboardData.stats.local.completed_assignments || 0}
                </span>
              </div>

              {/* Total Workers */}
              <div className="flex flex-col items-center px-3 py-1 bg-indigo-50 rounded-lg border border-indigo-200">
                <span className="text-xs text-indigo-700 font-medium">Total Workers</span>
                <span className="text-lg font-bold text-indigo-800">
                  {dashboardData.stats.local.total_workers || 0}
                </span>
              </div>
            </div>

            {/* Right: Refresh Button */}
            <button
              onClick={() => fetchDashboardData(true)}
              disabled={loading || refreshing}
              className="flex items-center px-4 py-2 text-sm font-medium bg-white text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-colors disabled:opacity-50 shadow-sm"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading || refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Updating...' : 'Refresh'}
            </button>
          </div>

          {/* Quick Filters Row */}
          <div className="border-t border-gray-100 pt-3">
            <div className="flex items-center mb-2">
              <Filter className="h-4 w-4 mr-2 text-blue-600" />
              <span className="text-xs font-semibold text-gray-700">Quick Filters</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
              <button
                onClick={() => navigateWithFilter('status', '0')}
                className="flex items-center justify-between px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg hover:border-yellow-400 hover:shadow transition-all group"
              >
                <span className="text-xs text-yellow-700 font-medium">Pending</span>
                <span className="text-lg font-bold text-yellow-700 group-hover:scale-110 transition-transform">{quickFilters.pending}</span>
              </button>

              <button
                onClick={() => navigateWithFilter('status', '1')}
                className="flex items-center justify-between px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg hover:border-blue-400 hover:shadow transition-all group"
              >
                <span className="text-xs text-blue-700 font-medium">Assigned</span>
                <span className="text-lg font-bold text-blue-700 group-hover:scale-110 transition-transform">{quickFilters.assigned}</span>
              </button>

              <button
                onClick={() => navigateWithFilter('status', '3')}
                className="flex items-center justify-between px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg hover:border-purple-400 hover:shadow transition-all group"
              >
                <span className="text-xs text-purple-700 font-medium">In Progress</span>
                <span className="text-lg font-bold text-purple-700 group-hover:scale-110 transition-transform">{quickFilters.inProgress}</span>
              </button>

              <button
                onClick={() => navigateWithFilter('status', 'paused')}
                className="flex items-center justify-between px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg hover:border-orange-400 hover:shadow transition-all group"
              >
                <span className="text-xs text-orange-700 font-medium">Paused</span>
                <span className="text-lg font-bold text-orange-700 group-hover:scale-110 transition-transform">{quickFilters.paused}</span>
              </button>

              <button
                onClick={() => navigateWithFilter('status', '6')}
                className="flex items-center justify-between px-3 py-2 bg-green-50 border border-green-200 rounded-lg hover:border-green-400 hover:shadow transition-all group"
              >
                <span className="text-xs text-green-700 font-medium">Completed</span>
                <span className="text-lg font-bold text-green-700 group-hover:scale-110 transition-transform">{quickFilters.completed}</span>
              </button>

              <button
                onClick={() => navigateWithFilter('priority', 'emergency')}
                className="flex items-center justify-between px-3 py-2 bg-red-50 border border-red-200 rounded-lg hover:border-red-400 hover:shadow transition-all group"
              >
                <span className="text-xs text-red-700 font-medium">Emergency</span>
                <span className="text-lg font-bold text-red-700 group-hover:scale-110 transition-transform">{quickFilters.emergency}</span>
              </button>

              <button
                onClick={() => navigateWithFilter('priority', 'urgent')}
                className="flex items-center justify-between px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg hover:border-orange-400 hover:shadow transition-all group"
              >
                <span className="text-xs text-orange-700 font-medium">Urgent</span>
                <span className="text-lg font-bold text-orange-700 group-hover:scale-110 transition-transform">{quickFilters.urgent}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Workers Current Location Map */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-3">
          <div className="relative" style={{ height: '500px' }}>
            <WorkersLocationMap workers={dashboardData.workers_locations} />
          </div>
        </div>

        {/* Status Distribution - Local Assignments Only */}
        <div className="mb-3">
          <JobCardStatsWidget jobCards={dashboardData.all_assignments} loading={loading} showOverviewCards={false} showPriorityBreakdown={false} />
        </div>

        {/* Bottom Grid: Recent Jobs & Active Workers - Compact */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {/* Recent Jobs */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-bold text-gray-800 flex items-center">
                  <Calendar className="h-4 w-4 mr-1.5 text-blue-600" />
                  Recent Local Assignments
                </h3>
                <button
                  onClick={() => navigate('/dashboard/mms/jobcards', { state: { activeTab: 'local' } })}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  View All →
                </button>
              </div>
            </div>
            <div className="divide-y divide-gray-200 max-h-80 overflow-y-auto">
              {dashboardData.recent_assignments.length > 0 ? (
                dashboardData.recent_assignments.map(assignment => {
                  const jobcardNo = assignment.JOBCARD_NO || assignment.jobcard_no;
                  const status = assignment.assignment_status || assignment.status || '';
                  const omStatus = assignment.OM_STATUS ? parseInt(assignment.OM_STATUS) : null;
                  const isCompleted = status.toLowerCase() === 'completed' || omStatus === 6;
                  const isPending = status.toLowerCase() === 'pending' || omStatus === 0;
                  
                  return (
                    <div key={assignment.assignment_id || jobcardNo} className="p-2.5 hover:bg-blue-50 transition-colors cursor-pointer">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-0.5">
                            <span className="text-xs font-bold text-blue-600">#{jobcardNo}</span>
                            <span className={`px-1.5 py-0.5 text-[10px] rounded-full ${
                              isPending ? 'bg-yellow-100 text-yellow-800' :
                              isCompleted ? 'bg-green-100 text-green-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {isPending ? 'Pending' :
                               isCompleted ? 'Completed' : 'Active'}
                            </span>
                          </div>
                          <div className="mb-0.5">
                            <p className="text-xs text-gray-700 line-clamp-1" title={assignment.JOB_DESCRIPTION || assignment.APPLICATION_DESCRIPTION || assignment.MACTIVITY_DESCRIPTION || ''}>
                              {assignment.JOB_DESCRIPTION || assignment.APPLICATION_DESCRIPTION || assignment.MACTIVITY_DESCRIPTION || 'No description'}
                            </p>
                            {assignment.APPLICATION_TYPE_NAME && (
                              <p className="text-[10px] text-gray-500 mt-0.5">
                                {assignment.APPLICATION_TYPE_NAME}
                              </p>
                            )}
                          </div>
                          {(assignment.PROP_REF || assignment.property_address || assignment.ADDRESS) && (
                            <p className="text-[10px] text-gray-500 flex items-center">
                              <MapPin className="h-2.5 w-2.5 mr-0.5" />
                              {assignment.PROP_REF || assignment.property_address || assignment.ADDRESS}
                            </p>
                          )}
                        </div>
                        {(assignment.ASSIGNED_TO_NAME || assignment.worker_name) && (
                          <div className="text-[10px] text-gray-500 ml-2 text-right">
                            <div className="flex items-center justify-end">
                              <Users className="h-2.5 w-2.5 mr-0.5" />
                              <span className="truncate max-w-[100px]">{assignment.ASSIGNED_TO_NAME || assignment.worker_name}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="p-6 text-center text-gray-500 text-sm">
                  No recent local assignments
                </div>
              )}
            </div>
          </div>

          {/* Active Workers - Compact */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-bold text-gray-800 flex items-center">
                  <Users className="h-4 w-4 mr-1.5 text-blue-600" />
                  Artisan Status
                </h3>
                <span className="text-xs font-semibold text-gray-600">
                  {dashboardData.workers_locations.reduce((sum, worker) => sum + (worker.active_jobs || 0), 0)} Active Jobs
                </span>
              </div>
            </div>
            <div className="divide-y divide-gray-200 max-h-80 overflow-y-auto">
              {dashboardData.workers_locations.length > 0 ? (
                dashboardData.workers_locations.map(worker => (
                  <div key={worker.worker_id} className="p-2.5 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center">
                          <Users className="h-3.5 w-3.5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-900">{worker.worker_name}</p>
                          <p className="text-[10px] text-gray-500">{worker.active_jobs || 0} active jobs</p>
                        </div>
                      </div>
                      <div className={`px-2 py-0.5 text-[10px] rounded-full ${
                        worker.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {worker.status || 'Active'}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-6 text-center text-gray-500 text-sm">
                  No active workers
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedMMSDashboard;
